/**
 * Het tabblad Woordenlijsten (stap 29): de woorden die een leerling oefent
 * voor hij een tekst leest, per tekst en niveau, en hun plaatjes.
 *
 * Zo gaat het (backend: services/woordenlijst.py en services/woordbeelden.py):
 *   1. zet 1 tot 3 plaatjes in het stijlanker (de stijl van alle woordplaatjes);
 *   2. kies een tekst en laat het model de lijsten voorstellen: eerst de
 *      kernwoorden (op elk niveau in de tekst), dan de moeilijke woorden van
 *      het niveau, hooguit 10 op M4 tot 24 op M8, alleen wat je kunt tekenen;
 *   3. bewerk een lijst als dat nodig is, en laat de plaatjes maken: vier per
 *      woord (de keuzeset), daarna kijkt Claude ze na;
 *   4. bekijk elk woord (rood: Claude vond iets), bewerk zo nodig de kaart,
 *      en keur het goed; dan de lijst. Pas dan ziet de leerling de oefening.
 * Het werk met het model loopt op de achtergrond; zolang er iets bezig is,
 * haalt dit tabblad het overzicht elke vijf seconden opnieuw op.
 *
 * De kosten (stap 29d): de beheerder kiest de resolutie (512 of 1K) en of de
 * plaatjes via de batchdienst van Google gaan (half geld, kan even duren) of
 * meteen. Voor "Maak de plaatjes" staat hoeveel plaatjes het worden en wat
 * dat ongeveer kost; één mislukt plaatje kan los opnieuw. Is de maandlimiet
 * van het Gemini-project bereikt of het tegoed op, dan staat dat bovenaan.
 *
 * Per woord: zegt de leerling het in de actieve stand, of kiest hij uit de
 * vier plaatjes (stap 29e)? Vast per woord; het advies komt uit de controle
 * (kiezen als Claude het plaatje wel koos maar de zin er niet mee aanvulde).
 */
import React, { useEffect, useRef, useState } from 'react';
import { ArrowDown, ArrowUp, Check, Image, RefreshCw, Sparkles, Trash2, Upload, X } from 'lucide-react';
import { beheerApi as api } from '../../../api/beheer';
import { Beeld } from '../../../components/Beeld';
import { Draaier, Laden } from '../../../components/Laden';
import { Venster } from '../../../components/Venster';

const ELKE_VIJF_SECONDEN = 5000;
const SOORTEN = ['voorwerp', 'persoon', 'dier', 'deel'];

export const TOESTAND = {
  kaart_bezig: { label: 'kaart wordt gemaakt', kleur: 'var(--blauw)', bezig: true },
  kaart_mislukt: { label: 'kaart mislukt', kleur: 'var(--fout)' },
  geen_kaart: { label: 'geen kaart (onderbroken)', kleur: 'var(--fout)' },
  nieuw: { label: 'nog geen plaatjes', kleur: 'var(--tekst-3)' },
  bezig: { label: 'plaatjes worden gemaakt', kleur: 'var(--blauw)', bezig: true },
  onderbroken: { label: 'onderbroken', kleur: 'var(--fout)' },
  mislukt: { label: 'plaatjes mislukt', kleur: 'var(--fout)' },
  klaar: { label: 'nakijken', kleur: 'var(--waarschuw-tekst)' },
  goedgekeurd: { label: 'goedgekeurd', kleur: 'var(--goed-tekst)' },
  ontbreekt: { label: 'niet in de woordenbank', kleur: 'var(--fout)' },
};
const LIJSTSTATUS = { bezig: 'wordt voorgesteld', concept: 'concept', mislukt: 'mislukt', goedgekeurd: 'goedgekeurd' };
const beeldpad = (id) => `/admin/word-images/${id}`;
/** "$0,29". */
export const dollar = (bedrag) => `$${bedrag.toFixed(2).replace('.', ',')}`;
/** Een prijs per plaatje in dollarcent: "2,25 cent". */
export const centen = (bedrag) => `${(Math.round(bedrag * 10000) / 100).toString().replace('.', ',')} dollarcent`;
const woorden = (n) => `${n} ${n === 1 ? 'woord' : 'woorden'}`;
const MODELNAAM = { 'gemini-3.1-flash-image': 'Nano Banana 2 (gemini-3.1-flash-image)', 'gemini-3-pro-image': 'Nano Banana Pro (gemini-3-pro-image)' };
const lijstBezig = (n) => n.lijst?.status === 'bezig';
const woordBezig = (w) => TOESTAND[w.toestand]?.bezig;

function WoordChip({ w, onKlik }) {
  const t = TOESTAND[w.toestand] || TOESTAND.nieuw;
  const rood = w.nakijken && w.toestand !== 'goedgekeurd';
  return (
    <button type="button" onClick={onKlik} data-testid={`woord-${w.woord}`} data-toestand={w.toestand}
      title={`${t.label}${rood ? ': Claude vond iets, kijk het na' : ''}${w.fout ? `: ${w.fout}` : ''}${w.kern ? '; kernwoord' : ''}${w.zeldzaam ? '; moeilijk woord' : ''}${w.in_tekst === false ? '; staat niet (meer) in de tekst' : ''}`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 8px', margin: '0 6px 6px 0', borderRadius: 6,
        cursor: 'pointer', fontSize: 13, background: 'var(--wit)', color: t.kleur,
        border: `${rood ? 2 : 1}px solid ${rood ? 'var(--fout)' : 'var(--lijn)'}`,
      }}>
      {w.doelbeeld && <Beeld pad={beeldpad(w.doelbeeld)} alt="" grootte={28} style={{ borderRadius: 4 }} />}
      {t.bezig && <Draaier grootte={10} />}
      {w.batch && <span className="text-small" title="bij de batchdienst van Google: half geld, kan even duren">batch</span>}
      <span style={{ fontWeight: w.kern ? 700 : 400 }}>{w.lidwoord ? `${w.lidwoord} ` : ''}{w.woord}</span>
      {w.toestand === 'goedgekeurd' && <Check size={12} />}
      {w.vorm === 'kiezen' && <span className="text-small" title="in de actieve stand kiest de leerling dit woord uit vier plaatjes">kiezen</span>}
      {w.in_tekst === false && <span title="staat niet in de tekst">≠</span>}
    </button>
  );
}

function LijstBewerken({ lijst, aantal, onBewaar, onSluit }) {
  const [woorden, setWoorden] = useState(lijst.woorden.map(w => ({ woord: w.woord })));
  const [nieuw, setNieuw] = useState({ woord: '', lidwoord: 'de', soort: 'voorwerp' });
  const [bezig, setBezig] = useState(false);
  const verplaats = (i, stap) => setWoorden((oud) => {
    const lijstje = [...oud];
    [lijstje[i], lijstje[i + stap]] = [lijstje[i + stap], lijstje[i]];
    return lijstje;
  });
  const voegToe = () => {
    if (!nieuw.woord.trim()) return;
    setWoorden(oud => [...oud, { ...nieuw, woord: nieuw.woord.trim().toLowerCase() }]);
    setNieuw({ ...nieuw, woord: '' });
  };
  const bewaar = async () => {
    setBezig(true);
    try { await onBewaar(woorden); } finally { setBezig(false); }
  };
  return (
    <div style={{ background: 'var(--vlak)', padding: 12, borderRadius: 8, marginTop: 8 }}>
      <p className="text-small text-muted mb-8">
        In deze volgorde krijgt de leerling de woorden; hooguit {aantal}. Een nieuw woord krijgt een kaart van het model.
      </p>
      {woorden.map((w, i) => (
        <div key={w.woord} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
          <span style={{ width: 24, textAlign: 'right' }} className="text-muted">{i + 1}.</span>
          <span style={{ flex: 1 }}>{w.lidwoord && !lijst.woorden.some(x => x.woord === w.woord) ? `${w.lidwoord} ` : ''}{w.woord}</span>
          <button type="button" className="btn btn-secondary" style={{ padding: '2px 6px' }} disabled={i === 0}
            onClick={() => verplaats(i, -1)} aria-label={`${w.woord} omhoog`}><ArrowUp size={12} /></button>
          <button type="button" className="btn btn-secondary" style={{ padding: '2px 6px' }} disabled={i === woorden.length - 1}
            onClick={() => verplaats(i, 1)} aria-label={`${w.woord} omlaag`}><ArrowDown size={12} /></button>
          <button type="button" className="btn btn-danger" style={{ padding: '2px 6px' }}
            onClick={() => setWoorden(oud => oud.filter((_, j) => j !== i))} aria-label={`${w.woord} weg`}><X size={12} /></button>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
        <select className="input" style={{ width: 'auto' }} value={nieuw.lidwoord} aria-label="Lidwoord"
          onChange={e => setNieuw({ ...nieuw, lidwoord: e.target.value })}>
          <option value="de">de</option><option value="het">het</option>
        </select>
        <input className="input" style={{ width: 180 }} placeholder="nieuw woord" value={nieuw.woord} aria-label="Nieuw woord"
          onChange={e => setNieuw({ ...nieuw, woord: e.target.value })} onKeyDown={e => { if (e.key === 'Enter') voegToe(); }} />
        <select className="input" style={{ width: 'auto' }} value={nieuw.soort} aria-label="Soort"
          onChange={e => setNieuw({ ...nieuw, soort: e.target.value })}>
          {SOORTEN.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <button type="button" className="btn btn-secondary" onClick={voegToe}>Voeg toe</button>
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button type="button" className="btn btn-primary" onClick={bewaar} disabled={bezig || woorden.length > aantal}>
          {bezig ? <Draaier /> : <Check size={14} />} Bewaar de lijst ({woorden.length}/{aantal})
        </button>
        <button type="button" className="btn btn-secondary" onClick={onSluit} disabled={bezig}>Annuleer</button>
      </div>
    </div>
  );
}

function Voorstel({ voorstel }) {
  if (!voorstel?.kandidaten?.length) return null;
  return (
    <details className="mt-8">
      <summary className="text-small" style={{ cursor: 'pointer' }}>
        Het voorstel: {voorstel.kandidaten.length} kandidaten op dit niveau (kern: op alle {voorstel.kern_over.length} goedgekeurde niveaus)
      </summary>
      <table className="mt-8" style={{ fontSize: 12 }}>
        <thead><tr><th>Woord</th><th>Soort</th><th>Kern</th><th>Moeilijk</th><th>In de lijst</th></tr></thead>
        <tbody>
          {voorstel.kandidaten.map(k => (
            <tr key={k.woord}>
              <td>{k.lidwoord ? `${k.lidwoord} ` : ''}{k.woord}</td>
              <td>{k.soort}</td>
              <td>{k.kern ? 'ja' : ''}</td>
              <td>{k.zeldzaam ? 'ja' : ''}</td>
              <td>{k.gekozen ? 'ja' : <span className="text-muted">{k.reden}</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </details>
  );
}

function Niveau({ n, tekstId, instellingen, onOverzicht, onOpenWoord, meldFout }) {
  const [bewerken, setBewerken] = useState(false);
  const [bezig, setBezig] = useState(false);
  const lijst = n.lijst;
  const doe = async (functie) => {
    setBezig(true);
    try {
      onOverzicht(await functie());
      return true;
    } catch (err) {
      meldFout(err.message);
      return false;
    } finally {
      setBezig(false);
    }
  };
  const zonderPlaatjes = (lijst?.woorden || []).filter(w => ['nieuw', 'mislukt', 'onderbroken'].includes(w.toestand));
  const aantalPlaatjes = zonderPlaatjes.reduce((som, w) => som + (w.plaatjes || 4), 0);
  const kosten = dollar(aantalPlaatjes * instellingen.prijs_per_plaatje);
  const maakPlaatjes = () => {
    const hoe = instellingen.wijze === 'batch' ? 'via de batchdienst van Google (half geld; het kan even duren)' : 'meteen';
    if (window.confirm(`${woorden(zonderPlaatjes.length)}, ${aantalPlaatjes} plaatjes, ongeveer ${kosten}, ${hoe}. Doorgaan?`)) {
      doe(() => api.maakPlaatjesVanLijst(tekstId, n.niveau));
    }
  };
  return (
    <div data-testid={`niveau-${n.niveau}`} style={{ borderTop: '1px solid var(--lijn)', padding: '12px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <strong style={{ width: 70 }}>{n.niveau}</strong>
        {!lijst ? (
          <span className="text-muted text-small">
            {n.tekst_goedgekeurd ? 'nog geen lijst' : 'de tekst is op dit niveau nog niet goedgekeurd'}
          </span>
        ) : (
          <>
            <span className={`badge ${lijst.klaar ? 'badge-success' : lijst.status === 'mislukt' ? 'badge-danger' : 'badge-warning'}`}>
              {lijstBezig(n) && <Draaier grootte={10} />} {lijst.klaar ? 'klaar voor de klas' : LIJSTSTATUS[lijst.status]}
            </span>
            <span className="text-small text-muted">{lijst.woorden.length} van hooguit {n.aantal} woorden</span>
            {lijst.verouderd && <span className="text-small" style={{ color: 'var(--waarschuw-tekst)' }}>
              de tekst op dit niveau is veranderd sinds het voorstel</span>}
            <span style={{ flex: 1 }} />
            {lijst.status !== 'bezig' && (
              <>
                {zonderPlaatjes.length > 0 && (
                  <button type="button" className="btn btn-secondary" style={{ padding: '4px 10px' }} disabled={bezig}
                    onClick={maakPlaatjes} title={`${aantalPlaatjes} plaatjes à ${centen(instellingen.prijs_per_plaatje)}`}>
                    <Image size={14} /> Maak de plaatjes ({woorden(zonderPlaatjes.length)}, ± {kosten})
                  </button>
                )}
                <button type="button" className="btn btn-secondary" style={{ padding: '4px 10px' }} disabled={bezig}
                  onClick={() => setBewerken(b => !b)}>Bewerk de lijst</button>
                {lijst.status !== 'goedgekeurd' && (
                  <button type="button" className="btn btn-primary" style={{ padding: '4px 10px' }} disabled={bezig}
                    onClick={() => doe(() => api.keurWoordenlijstGoed(tekstId, n.niveau))}>
                    <Check size={14} /> Keur de lijst goed
                  </button>
                )}
                <button type="button" className="btn btn-danger" style={{ padding: '4px 8px' }} disabled={bezig}
                  aria-label={`Lijst ${n.niveau} weggooien`} title="De lijst weggooien (de woorden blijven in de woordenbank)"
                  onClick={() => { if (window.confirm(`De woordenlijst op ${n.niveau} weggooien?`)) doe(() => api.gooiWoordenlijstWeg(tekstId, n.niveau)); }}>
                  <Trash2 size={14} />
                </button>
              </>
            )}
          </>
        )}
      </div>
      {lijst?.fout && <div className="alert alert-error mt-8">{lijst.fout}</div>}
      {lijst && lijst.woorden.length > 0 && (
        <div className="mt-8">{lijst.woorden.map(w => <WoordChip key={w.woord} w={w} onKlik={() => w.id && onOpenWoord(w.id)} />)}</div>
      )}
      {bewerken && lijst && (
        <LijstBewerken lijst={lijst} aantal={n.aantal} onSluit={() => setBewerken(false)}
          onBewaar={async (woorden) => { if (await doe(() => api.bewaarWoordenlijst(tekstId, n.niveau, woorden))) setBewerken(false); }} />
      )}
      {lijst && <Voorstel voorstel={lijst.voorstel} />}
    </div>
  );
}

const lijstNaarTekst = (lijst) => (lijst || []).join(', ');
/** "De bijen (M4, M5), Rembrandt (E5)": waar een woord gebruikt wordt. */
const perTekst = (gebruikt) => {
  const teksten = {};
  gebruikt.forEach(g => { (teksten[g.tekst] = teksten[g.tekst] || []).push(g.niveau); });
  return Object.entries(teksten).map(([tekst, niveaus]) => `${tekst} (${niveaus.join(', ')})`).join(', ');
};
const tekstNaarLijst = (tekst) => tekst.split(',').map(s => s.trim().toLowerCase()).filter(Boolean);

function Controle({ controle }) {
  if (!controle) return null;
  if (controle.fout) return <div className="alert alert-error">{controle.fout}</div>;
  const teken = (goed) => <span style={{ color: goed ? 'var(--goed-2)' : 'var(--fout)', fontWeight: 700 }}>{goed ? '✓' : '✗'}</span>;
  return (
    <div className="text-small" data-testid="controle">
      <strong>Claude ({controle.model})</strong>
      <ul style={{ margin: '4px 0 0 18px' }}>
        {controle.opties.map(o => <li key={o.woord}>{teken(o.klopt)} het plaatje van <em>{o.woord}</em> heet volgens Claude: {o.antwoord}</li>)}
        <li>{teken(controle.kiezen.klopt)} bij het woord koos Claude: {controle.kiezen.antwoord} (getoond: {controle.kiezen.volgorde.join(', ')})</li>
        <li>{teken(!controle.opvallen.lekt)} valt er een plaatje op? {controle.opvallen.antwoord}
          {controle.opvallen.valt_op && ` (${controle.opvallen.valt_op})`}{controle.opvallen.lekt && ': het goede plaatje valt op, het item lekt'}</li>
        <li>{teken(controle.zin.klopt)} de zin aangevuld bij het plaatje: {controle.zin.antwoord}</li>
      </ul>
    </div>
  );
}

function WoordVenster({ woordId, instellingen, onSluit, onVeranderd, meldFout }) {
  const [woord, setWoord] = useState(null);
  const [kaart, setKaart] = useState(null);
  const [bezig, setBezig] = useState(false);

  const zet = (w) => {
    setWoord(w);
    setKaart({ lidwoord: w.lidwoord || 'de', soort: w.soort, zin: w.zin || '', goede_vormen: lijstNaarTekst(w.goede_vormen),
      afleiders: lijstNaarTekst(w.afleiders),
      keuzeset: w.keuzeset || { soort: 'los', basis: '', opties: [0, 1, 2, 3].map(i => ({ woord: i === 0 ? w.woord : '', beschrijving: '' })) } });
  };
  useEffect(() => { api.haalWoord(woordId).then(zet).catch(err => meldFout(err.message)); }, [woordId]); // eslint-disable-line react-hooks/exhaustive-deps

  // zolang het model bezig is: elke vijf seconden opnieuw
  const isBezig = woord && woordBezig(woord);
  useEffect(() => {
    if (!isBezig) return undefined;
    const klok = setInterval(() => api.haalWoord(woordId).then((w) => { if (!woordBezig(w)) { zet(w); onVeranderd(); } else setWoord(w); }).catch(() => {}),
      ELKE_VIJF_SECONDEN);
    return () => clearInterval(klok);
  }, [isBezig, woordId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!woord || !kaart) return <Venster breed={900}><Laden tekst="Woord laden..." /></Venster>;

  const doe = async (functie) => {
    setBezig(true);
    try {
      zet(await functie());
      onVeranderd();
    } catch (err) {
      meldFout(err.message);
    } finally {
      setBezig(false);
    }
  };
  const optie = (i, veld, waarde) => setKaart(k => ({ ...k, keuzeset: { ...k.keuzeset,
    opties: k.keuzeset.opties.map((o, j) => (j === i ? { ...o, [veld]: waarde } : o)) } }));
  const bewaar = () => {
    const veranderdeSet = JSON.stringify(kaart.keuzeset) !== JSON.stringify(woord.keuzeset) || kaart.soort !== woord.soort;
    if (veranderdeSet && woord.opties.length && !window.confirm('Een andere keuzeset of soort past niet bij de plaatjes: die gaan weg. Doorgaan?')) return;
    doe(() => api.bewaarWoord(woord.id, { ...kaart, goede_vormen: tekstNaarLijst(kaart.goede_vormen), afleiders: tekstNaarLijst(kaart.afleiders) }));
  };
  const t = TOESTAND[woord.toestand] || TOESTAND.nieuw;
  const bewerk = kaart.keuzeset.soort === 'bewerk';
  const advies = woord.advies || { vorm: 'spreken', reden: '' };

  return (
    <Venster breed={960} vanBoven>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <h2 style={{ margin: 0 }}>{woord.lidwoord} {woord.woord}</h2>
        <span style={{ color: t.kleur }}>{t.bezig && <Draaier grootte={12} />} {t.label}</span>
        <span style={{ flex: 1 }} />
        <button type="button" className="btn btn-secondary" onClick={onSluit}><X size={16} /> Sluiten</button>
      </div>
      {woord.fout && <div className="alert alert-error">{woord.fout}</div>}
      {woord.context && <p className="text-small text-muted mb-8">In de tekst: “{woord.context}”</p>}
      {woord.gebruikt.length > 0 && (
        <p className="text-small text-muted mb-8">In de lijsten van: {perTekst(woord.gebruikt)}. Een wijziging geldt overal.</p>
      )}

      {woord.opties.length > 0 && (
        <div className="mb-16">
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            {woord.basis && (
              <figure style={{ textAlign: 'center', opacity: 0.85 }}>
                <Beeld pad={beeldpad(woord.basis)} alt="basis" grootte={150} style={{ border: '1px dashed var(--lijn-3)' }} />
                <figcaption className="text-small text-muted">basis{woord.paar ? ' (zonder)' : ''}</figcaption>
              </figure>
            )}
            {woord.opties.map((o, i) => (
              <figure key={o.id} style={{ textAlign: 'center' }}>
                <Beeld pad={beeldpad(o.id)} alt={o.woord} grootte={150}
                  style={{ border: i === 0 ? '3px solid var(--goed-2)' : '1px solid var(--lijn)' }} />
                <figcaption className="text-small">
                  {o.woord}{i === 0 ? ' (het woord)' : ''}{o.breedte ? <span className="text-muted"> · {o.breedte} px</span> : null}
                  <button type="button" className="btn btn-secondary" style={{ padding: '0 6px', marginLeft: 6 }}
                    disabled={bezig || isBezig} aria-label={`${o.woord} opnieuw`}
                    title={`Alleen dit plaatje opnieuw, meteen (± ${centen(instellingen.prijs_direct)})`}
                    onClick={() => doe(() => api.maakEenPlaatjeOpnieuw(woord.id, o.id))}><RefreshCw size={11} /></button>
                </figcaption>
              </figure>
            ))}
          </div>
          <p className="text-small text-muted mt-8">Gemaakt met {woord.beeldmodel}. Zo ziet de leerling ze in de passieve stand, in een andere volgorde.
            {woord.paar && ' In de actieve stand ziet hij eerst de basis, dan het woord.'}</p>
          <Controle controle={woord.controle} />
        </div>
      )}

      <div className="mb-16" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <label htmlFor="wk-vorm">In de actieve stand</label>
        <select id="wk-vorm" className="input" style={{ width: 'auto' }} value={woord.vorm || 'spreken'} disabled={bezig}
          onChange={e => doe(() => api.bewaarWoord(woord.id, { vorm: e.target.value }))}>
          <option value="spreken">zegt hij de zin bij het plaatje</option>
          <option value="kiezen">kiest hij uit de vier plaatjes</option>
        </select>
        <span className="text-small text-muted" data-testid="vorm-advies" style={{ flexBasis: '100%' }}>
          {woord.vorm_gekozen ? (woord.vorm_gekozen !== advies.vorm
            ? `Advies: ${advies.vorm === 'kiezen' ? 'kiezen' : 'zeggen'}. ${advies.reden}` : advies.reden)
            : `Advies, vast bij het goedkeuren: ${advies.reden}`}
          {' '}Vast per woord: elke leerling krijgt hetzelfde item; de leerkracht ziet gezegd en gekozen apart.
        </span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 8, alignItems: 'center' }}>
        <label htmlFor="wk-lidwoord">Lidwoord, soort</label>
        <div style={{ display: 'flex', gap: 8 }}>
          <select id="wk-lidwoord" className="input" style={{ width: 'auto' }} value={kaart.lidwoord}
            onChange={e => setKaart({ ...kaart, lidwoord: e.target.value })}>
            <option value="de">de</option><option value="het">het</option>
          </select>
          <select className="input" style={{ width: 'auto' }} value={kaart.soort} aria-label="Soort"
            onChange={e => setKaart({ ...kaart, soort: e.target.value })}>
            {SOORTEN.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <label htmlFor="wk-zin">Zin</label>
        <input id="wk-zin" className="input" value={kaart.zin} onChange={e => setKaart({ ...kaart, zin: e.target.value })}
          placeholder="Dit is een zware ___." />
        <label htmlFor="wk-vormen">Goed is</label>
        <input id="wk-vormen" className="input" value={kaart.goede_vormen}
          onChange={e => setKaart({ ...kaart, goede_vormen: e.target.value })} />
        <label htmlFor="wk-afleiders">Foute woorden</label>
        <input id="wk-afleiders" className="input" value={kaart.afleiders}
          onChange={e => setKaart({ ...kaart, afleiders: e.target.value })} title="voor de spraakherkenning: wat een kind in plaats van het woord kan zeggen" />
        <label htmlFor="wk-set">Keuzeset</label>
        <select id="wk-set" className="input" style={{ width: 'auto' }} value={kaart.keuzeset.soort}
          onChange={e => setKaart({ ...kaart, keuzeset: { ...kaart.keuzeset, soort: e.target.value } })}>
          <option value="los">los: elk plaatje apart</option>
          <option value="bewerk">bewerk: één basis, vier bewerkingen</option>
        </select>
        {bewerk && (
          <>
            <label htmlFor="wk-basis">Basis</label>
            <input id="wk-basis" className="input" value={kaart.keuzeset.basis || ''}
              onChange={e => setKaart({ ...kaart, keuzeset: { ...kaart.keuzeset, basis: e.target.value } })}
              placeholder="een man met kort haar, zonder baard, snor, bril of hoed, van voren" />
          </>
        )}
        {kaart.keuzeset.opties.map((o, i) => (
          <React.Fragment key={i}>
            <input className="input" value={o.woord} aria-label={`Optie ${i + 1}: woord`} disabled={i === 0}
              onChange={e => optie(i, 'woord', e.target.value)} />
            <input className="input" value={o.beschrijving} aria-label={`Optie ${i + 1}: ${bewerk ? 'bewerking' : 'beschrijving'}`}
              onChange={e => optie(i, 'beschrijving', e.target.value)}
              placeholder={bewerk ? 'geef de man een volle baard' : 'een rechthoekige reiskoffer met een handvat'} />
          </React.Fragment>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
        <button type="button" className="btn btn-secondary" onClick={bewaar} disabled={bezig || isBezig}><Check size={14} /> Bewaar de kaart</button>
        <button type="button" className="btn btn-secondary" disabled={bezig || isBezig}
          title={`${woord.plaatjes} plaatjes, ± ${dollar(woord.plaatjes * instellingen.prijs_per_plaatje)} (${instellingen.wijze === 'batch' ? 'batchdienst' : 'meteen'})`}
          onClick={() => doe(() => api.maakPlaatjes(woord.id))}><Image size={14} /> {woord.opties.length ? 'Maak de hele set opnieuw' : 'Maak de plaatjes'}</button>
        {woord.opties.length > 0 && (
          <button type="button" className="btn btn-secondary" disabled={bezig || isBezig}
            onClick={() => doe(() => api.controleerPlaatjes(woord.id))}><RefreshCw size={14} /> Laat Claude opnieuw kijken</button>
        )}
        <button type="button" className="btn btn-secondary" disabled={bezig || isBezig}
          onClick={() => { if (window.confirm('Het model maakt de kaart opnieuw; de plaatjes gaan weg. Doorgaan?')) doe(() => api.maakWoordkaartOpnieuw(woord.id)); }}>
          <Sparkles size={14} /> Nieuwe kaart van het model</button>
        <span style={{ flex: 1 }} />
        {woord.toestand === 'klaar' && (
          <button type="button" className="btn btn-primary" disabled={bezig}
            onClick={() => doe(() => api.keurWoordGoed(woord.id))}><Check size={14} /> Keur goed</button>
        )}
      </div>
    </Venster>
  );
}

function Stijlanker({ instellingen, onInstellingen, meldFout }) {
  const kiezer = useRef(null);
  const doe = async (functie) => {
    try { onInstellingen(await functie()); } catch (err) { meldFout(err.message); }
  };
  return (
    <div className="mb-16" style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-start' }}>
      <div>
        <h3 className="mb-8">Stijlanker</h3>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {instellingen.ankers.map(id => (
            <div key={id} style={{ position: 'relative' }}>
              <Beeld pad={beeldpad(id)} alt="stijlanker" grootte={80} style={{ border: '1px solid var(--lijn)' }} />
              <button type="button" className="btn btn-danger" aria-label="Uit het stijlanker"
                style={{ position: 'absolute', top: -6, right: -6, padding: '0 4px', lineHeight: 1 }}
                onClick={() => doe(() => api.verwijderAnker(id))}><X size={12} /></button>
            </div>
          ))}
          {instellingen.ankers.length < instellingen.max_ankers && (
            <>
              <button type="button" className="btn btn-secondary" onClick={() => kiezer.current?.click()}><Upload size={14} /> Plaatje toevoegen</button>
              <input ref={kiezer} type="file" accept="image/png,image/jpeg,image/webp" hidden data-testid="anker-kiezer"
                onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) doe(() => api.voegAnkerToe(f)); }} />
            </>
          )}
        </div>
        <p className="text-small text-muted mt-8" style={{ maxWidth: 420 }}>
          1 tot 3 plaatjes in de stijl die alle woordplaatjes krijgen (bijvoorbeeld de theepotten uit de beeldproef). Een nieuw anker geldt voor nieuwe plaatjes.
        </p>
      </div>
      <div>
        <h3 className="mb-8">De plaatjes</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'auto auto', justifyContent: 'start', gap: 8, alignItems: 'center' }}>
          <label htmlFor="wb-model">Model</label>
          <select id="wb-model" className="input" style={{ width: 'auto' }} value={instellingen.beeldmodel}
            onChange={e => doe(() => api.zetWoordbeeldInstellingen({ beeldmodel: e.target.value }))}>
            {instellingen.beeldmodellen.map(m => <option key={m} value={m}>{MODELNAAM[m] || m}</option>)}
          </select>
          <label htmlFor="wb-resolutie">Resolutie</label>
          <select id="wb-resolutie" className="input" style={{ width: 'auto' }} value={instellingen.resolutie}
            disabled={instellingen.beeldmodel === 'gemini-3-pro-image'} title={instellingen.beeldmodel === 'gemini-3-pro-image' ? 'Pro maakt altijd 1K' : ''}
            onChange={e => doe(() => api.zetWoordbeeldInstellingen({ resolutie: e.target.value }))}>
            {instellingen.resoluties.map(r => <option key={r} value={r}>{r === '512' ? '512 pixels (genoeg voor het scherm)' : r}</option>)}
          </select>
          <label htmlFor="wb-wijze">Maken</label>
          <select id="wb-wijze" className="input" style={{ width: 'auto' }} value={instellingen.wijze}
            onChange={e => doe(() => api.zetWoordbeeldInstellingen({ wijze: e.target.value }))}>
            <option value="batch">via de batchdienst (half geld, kan even duren)</option>
            <option value="direct">meteen</option>
          </select>
        </div>
        <p className="text-small text-muted mt-8" data-testid="prijs">
          Ongeveer {centen(instellingen.prijs_per_plaatje)} per plaatje{instellingen.wijze === 'batch' ? ` (meteen: ${centen(instellingen.prijs_direct)})` : ''};
          een woord is 4 plaatjes, met een basis 5. Prijzen van Google (5 november), zonder het nadenken van het model.
        </p>
        {instellingen.limiet && <div className="alert alert-error mt-8">{instellingen.limiet}</div>}
        {!instellingen.gemini && <p className="text-small mt-8" style={{ color: 'var(--fout)' }}>Er is geen Gemini-sleutel: vul hem in bij LLM Config.</p>}
        {!instellingen.spraak && <p className="text-small mt-8" style={{ color: 'var(--waarschuw-tekst)' }}>Er is geen Mistral-sleutel: de actieve stand (spreken) werkt dan niet; leerlingen krijgen de passieve.</p>}
      </div>
    </div>
  );
}

export function Woordenlijsten({ actief, meldFout }) {
  const [teksten, setTeksten] = useState(null);
  const [tekstId, setTekstId] = useState('');
  const [overzicht, setOverzicht] = useState(null);
  const [instellingen, setInstellingen] = useState(null);
  const [openWoord, setOpenWoord] = useState(null);
  const [voorstellen, setVoorstellen] = useState(false);

  const zetOverzicht = (data) => {
    setOverzicht(data);
    if (data?.instellingen) setInstellingen(data.instellingen);
  };
  const herlaad = async (id = tekstId) => {
    if (!id) return;
    zetOverzicht(await api.haalWoordenlijsten(id));
  };

  useEffect(() => {
    if (!actief) return;
    api.haalWoordenlijstTeksten().then(setTeksten).catch(err => meldFout(err.message));
    api.haalStijlanker().then(setInstellingen).catch(err => meldFout(err.message));
    if (tekstId) herlaad().catch(err => meldFout(err.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- alleen bij het openen van het tabblad
  }, [actief]);

  const ergensBezig = Boolean(overzicht?.niveaus.some(n => lijstBezig(n) || (n.lijst?.woorden || []).some(woordBezig)));
  useEffect(() => {
    if (!actief || !ergensBezig) return undefined;
    const klok = setInterval(() => herlaad().catch(() => {}), ELKE_VIJF_SECONDEN);
    return () => clearInterval(klok);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- herlaad verandert niet van betekenis
  }, [actief, ergensBezig, tekstId]);

  const kiesTekst = (id) => {
    setTekstId(id);
    setOverzicht(null);
    if (id) herlaad(id).catch(err => meldFout(err.message));
  };

  const stelVoor = async () => {
    const heeftLijsten = overzicht.niveaus.some(n => n.lijst);
    if (heeftLijsten && !window.confirm('Een nieuw voorstel vervangt elke lijst van deze tekst, ook een goedgekeurde; de leerlingen zien de oefening pas weer als je hem opnieuw goedkeurt. Doorgaan?')) return;
    setVoorstellen(true);
    try {
      zetOverzicht(await api.stelWoordenlijstenVoor(tekstId));
    } catch (err) {
      meldFout(err.message);
    } finally {
      setVoorstellen(false);
    }
  };

  if (!teksten || !instellingen) return <Laden tekst="Woordenlijsten laden..." />;
  const gekozen = teksten.find(t => t.id === tekstId);

  return (
    <div className="card">
      <h2>Woordenlijsten</h2>
      <p className="text-muted mb-16">
        Voor hij een tekst leest, kan een leerling de woorden oefenen: passief (hij hoort het woord en kiest uit vier
        plaatjes) of actief (hij ziet het plaatje en zegt de zin), zoals de leerkracht instelt. Per tekst en niveau een
        vaste lijst: eerst de kernwoorden (<strong>vet</strong>: ze staan op elk niveau in de tekst), dan de moeilijke
        woorden van het niveau; hooguit 10 op M4 tot 24 op M8. Pas als je elk woord en de lijst goedkeurt, ziet de
        leerling de oefening.
      </p>

      <Stijlanker instellingen={instellingen} onInstellingen={setInstellingen} meldFout={meldFout} />

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <label htmlFor="wl-tekst"><strong>Tekst</strong></label>
        <select id="wl-tekst" className="input" style={{ width: 'auto', minWidth: 280 }} value={tekstId} onChange={e => kiesTekst(e.target.value)}>
          <option value="">Kies een tekst</option>
          {teksten.map(t => <option key={t.id} value={t.id}>{t.titel} ({t.klaar} van {t.niveaus} niveaus klaar)</option>)}
        </select>
        {gekozen && overzicht && (
          <button type="button" className="btn btn-primary" onClick={stelVoor} disabled={voorstellen || overzicht.niveaus.some(lijstBezig)}>
            {voorstellen ? <Draaier /> : <Sparkles size={14} />} {overzicht.niveaus.some(n => n.lijst) ? 'Stel de lijsten opnieuw voor' : 'Stel de woordenlijsten voor'}
          </button>
        )}
      </div>
      {teksten.length === 0 && <p className="text-muted mt-16">Nog geen tekst met een goedgekeurd niveau (tabblad Niveauteksten).</p>}

      {tekstId && !overzicht && <Laden />}
      {overzicht && (
        <div className="mt-16">
          {overzicht.niveaus.map(n => (
            <Niveau key={n.niveau} n={n} tekstId={tekstId} instellingen={instellingen} onOverzicht={zetOverzicht}
              onOpenWoord={setOpenWoord} meldFout={meldFout} />
          ))}
        </div>
      )}

      {openWoord && (
        <WoordVenster woordId={openWoord} instellingen={instellingen} meldFout={meldFout} onSluit={() => setOpenWoord(null)}
          onVeranderd={() => herlaad().catch(() => {})} />
      )}
    </div>
  );
}
